import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { CheckCircle, ArrowRight, MessageSquarePlus, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import AppMenu from '../components/AppMenu';
import AppFooter from '../components/AppFooter';
import { CATEGORIES } from '../lib/categoryConfig';
import { CatIcon } from '../lib/iconMap';
import apiClient from '../api/client';

/* ─── contact dialog ────────────────────────────────────────────────────── */

function OtherRequestDialog({ open, onClose }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Namn och telefonnummer krävs.');
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/quick-leads', {
        contact_name: form.name.trim(),
        contact_phone: form.phone.trim(),
        contact_email: form.email.trim() || undefined,
        contact_city: form.address.trim() || undefined,
        // Required by API schema; n/a for general leads – mark clearly in notes
        kitchen_size: 'övrigt',
        high_cabinets: 'övrigt',
        price_model: 'fastpris',
        notes: `[Övrigt arbete via startsidan] ${form.message.trim()}`.trim(),
      });
      setSent(true);
    } catch {
      toast.error('Något gick fel. Försök igen eller ring oss.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => { setSent(false); setForm({ name: '', phone: '', email: '', address: '', message: '' }); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="kh-contact-dialog sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Beskriv ditt projekt</DialogTitle>
          <DialogDescription>
            Berätta kort om arbetet, så kopplas dina kontaktuppgifter till projektbeskrivningen.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center gap-3 text-center py-6">
            <div className="h-12 w-12 rounded-full bg-[var(--kh-evergreen)] flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <p className="font-bold text-primary text-base">Tack! Din förfrågan är skickad.</p>
            <p className="text-sm text-muted-foreground">Vi kontaktar dig via uppgifterna du lämnade.</p>
            <Button variant="outline" className="mt-2" onClick={handleClose}>Stäng</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 mt-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="other-project-name" className="text-sm font-semibold">Namn *</Label>
                <Input id="other-project-name" autoComplete="name" placeholder="För- och efternamn" value={form.name} onChange={(e) => update('name', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="other-project-phone" className="text-sm font-semibold">Telefon *</Label>
                <Input id="other-project-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="070-123 45 67" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="other-project-email" className="text-sm font-semibold">E-post</Label>
              <Input id="other-project-email" type="email" autoComplete="email" placeholder="namn@exempel.se" value={form.email} onChange={(e) => update('email', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="other-project-address" className="text-sm font-semibold">Adress</Label>
              <Input id="other-project-address" autoComplete="street-address" placeholder="Gatuadress, ort" value={form.address} onChange={(e) => update('address', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="other-project-message" className="text-sm font-semibold">Beskriv kort vad du behöver hjälp med</Label>
              <textarea
                id="other-project-message"
                placeholder="t.ex. Bygga altan, Måla fasad, Installera bastu"
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px]"
              />
            </div>
            <Button type="submit" disabled={loading} className="kh-button w-full">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Skickar</> : 'Skicka förfrågan'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── main page ─────────────────────────────────────────────────────────── */

const STEPS = [
  { n: '01', title: 'Välj arbetet', desc: 'Börja med den tjänst som passar projektet.' },
  { n: '02', title: 'Beskriv omfattningen', desc: 'Svara på frågor och lägg till underlag när du har det.' },
  { n: '03', title: 'Se prisbilden', desc: 'Gå igenom prisindikationen innan du skickar en förfrågan.' },
];

const HERO_IMAGE = '/offer-hero-editorial.jpg';

export default function LandingPage() {
  const navigate = useNavigate();
  const [otherOpen, setOtherOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>Klart Hemma | Prisindikation för ditt projekt</title>
        <meta name="description" content="Beskriv ditt renoverings- eller montageprojekt och se en prisindikation från Klart Hemma." />
        <meta property="og:title" content="Klart Hemma | Prisindikation för ditt projekt" />
        <meta property="og:description" content="Välj tjänst, beskriv omfattningen och se en prisbild för ditt projekt." />
      </Helmet>

      <div className="kh-offer-shell pb-16 sm:pb-0">
        <AppMenu />

        <section className="kh-offer-hero" aria-labelledby="kh-offer-heading">
          <div className="kh-offer-hero-copy">
            <h1 id="kh-offer-heading">Vad vill du förändra hemma?</h1>
            <p>Välj tjänst, beskriv omfattningen och se en prisindikation för projektet. Du kan komplettera uppgifterna när du har mer underlag.</p>
            <div className="kh-offer-hero-actions">
              <Button type="button" className="kh-button" onClick={() => navigate('/wizard')}>
                Börja beskriva projektet <ArrowRight className="h-4 w-4" />
              </Button>
              <a className="kh-text-link" href="#tjanster">Se alla tjänster <ChevronRight className="h-4 w-4" /></a>
            </div>
          </div>
          <figure className="kh-offer-hero-photo">
            <img src={HERO_IMAGE} alt="Ljust kök i ek med matplats och genomtänkta detaljer" loading="eager" />
            <figcaption className="kh-photo-caption">Renovering och montage i Skåne</figcaption>
          </figure>
        </section>

        <section id="tjanster" className="kh-service-section" aria-labelledby="kh-services-heading">
          <div className="kh-section-inner">
            <header className="kh-section-heading">
              <div>
                <p className="kh-eyebrow">Tjänster</p>
                <h2 id="kh-services-heading">Välj arbetet du planerar</h2>
              </div>
              <p>Välj den kategori som ligger närmast ditt projekt. I nästa steg beskriver du vad som ska göras.</p>
            </header>
          <div className="kh-category-grid">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => navigate(cat.route)}
                className="kh-service-card group"
              >
                  <div className="kh-service-icon">
                    <CatIcon name={cat.icon} className="h-5 w-5" />
                  </div>
                  <h3>{cat.label}</h3>
                  <p>
                    {cat.seoDescription.split('.')[0].trim()}.
                  </p>
                  <span className="kh-card-link">
                    Beskriv projektet
                    <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
              </button>
            ))}
          </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-10 sm:px-6" aria-label="Annat projekt">
          <div className="kh-other-request">
            <div className="flex items-center gap-3">
              <div className="kh-service-icon mb-0 shrink-0">
                <MessageSquarePlus className="h-4 w-4" />
              </div>
              <p>
                Hittar du inte rätt tjänst?{' '}
                <span>Beskriv projektet så tar vi del av din förfrågan.</span>
              </p>
            </div>
            <Button
              type="button"
              onClick={() => setOtherOpen(true)}
              className="kh-button"
            >
              Beskriv annat projekt
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        </section>

        <OtherRequestDialog open={otherOpen} onClose={() => setOtherOpen(false)} />

        <section id="hur-det-funkar" className="kh-steps-section">
          <div className="max-w-5xl mx-auto">
            <p className="kh-eyebrow">Så fungerar det</p>
            <h2>Från första uppgift till prisbild</h2>
            <div className="kh-step-grid">
              {STEPS.map((s) => (
                <article className="kh-step" key={s.n}>
                  <span className="kh-step-number">{s.n}</span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <AppFooter />

        {/* Sticky mobile CTA */}
        <div className="kh-mobile-cta fixed bottom-0 inset-x-0 z-40 sm:hidden border-t px-4 py-3">
          <Button
            className="kh-button w-full h-12 text-base"
            onClick={() => navigate('/wizard')}
          >
            Börja med ditt projekt
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  );
}
